import i18next from "src/i18n/config";
import { invalidateOnSuccess, transformErrorResponse } from "src/store/helpers";
import { ApiResponse, BasicApiResponse } from "src/types";

import { BaseQueryFn, EndpointBuilder } from "@reduxjs/toolkit/query";
import RouteService from "@services/routeService";

type FdroidsBuilder = EndpointBuilder<BaseQueryFn, "Fdroids", "fdroidsApi">;

type GetFdroidsParams = {
    subscriptionId?: number;
};

function getFdroids(builder: FdroidsBuilder) {
    return builder.query<API.FdroidRepo[], GetFdroidsParams>({
        providesTags: ["Fdroids"],
        transformErrorResponse: (err) => transformErrorResponse(err, i18next.t("common:error.technicalDifficulties.message")),
        transformResponse: (response: ApiResponse<API.FdroidRepo[]>) => response.model,
        query: async (params) => {
            if (!params.subscriptionId) {
                throw new Error("Subscription ID is required");
            }
            const base = await RouteService.getSubscriptionsRoute();
            return {
                url: `${base}${params.subscriptionId}/repos`,
            };
        },
    });
}

export type CreateFdroidRepoPayload = Omit<API.FdroidRepo, "id"> & {
    password: string;
};

function createFdroid(builder: FdroidsBuilder) {
    return builder.mutation<API.FdroidRepo, CreateFdroidRepoPayload>({
        invalidatesTags: invalidateOnSuccess("Fdroids"),
        transformErrorResponse: (err) => transformErrorResponse(err, i18next.t("common:error.technicalDifficulties.message")),
        transformResponse: (response: ApiResponse<API.FdroidRepo>) => response.model,
        query: async (payload) => {
            const base = await RouteService.getSubscriptionsRoute();
            return {
                url: `${base}${payload.subscriptionId}/repos`,
                method: "POST",
                data: payload,
                headers: { "content-type": "application/json" },
            };
        },
    });
}

type DeleteFdroidRepoParams = {
    subscriptionId: number;
    repoId: number;
};

function deleteFdroid(builder: FdroidsBuilder) {
    return builder.mutation<BasicApiResponse, DeleteFdroidRepoParams>({
        invalidatesTags: invalidateOnSuccess("Fdroids"),
        transformErrorResponse: (err) => transformErrorResponse(err, i18next.t("common:error.technicalDifficulties.message")),
        query: async (params) => {
            const base = await RouteService.getSubscriptionsRoute();
            return {
                url: `${base}${params.subscriptionId}/repos/${params.repoId}`,
                method: "DELETE",
                headers: { "content-type": "application/json" },
            };
        },
    });
}

export default { createFdroid, deleteFdroid, getFdroids };
